/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Popover, Spin, Form, Input, Carousel, App, Space } from 'antd'
import { LockOutlined, MobileOutlined } from '@ant-design/icons'
import AntdProvider from '@/components/AntdProvider'
import * as Sentry from '@sentry/react'
import classnames from 'classnames'
import {
  fetchVerifyCodeV3,
  fetchVerifyCodeShuMei,
  loginWithMobile,
  loginWithWechat,
  finishNickname,
  wechatBindMobile,
  verifySMSCode,
  finishGroupInfo,
  fetchVerifyCodeV3Encrypt,
  fetchVerifyCodeShuMeiEncrypt,
  loginWithMobileEncrypt
} from '../../actions/main'
import { setGroupId } from '../../actions/group'
import sensors from '../../utils/sensors'
import parseQueryString from '../../utils/parseQueryString'
import CONFIG from '../../constants/config'
import { capturePromiseException } from '../../utils/sentryUtils'
import { useNavigate } from 'react-router-dom'

import todaycam_logo from '../../images/todaycam_logo.png'
import qr_svg from '../../images/qr.svg?url'
import wx_qr_corner from '../../images/wx_qr_corner.png'
import download_client from '../../images/download-client/login-page-button.png'
import logo from '../../images/logo.png'

import InputName from './InputName'
import BindMobile from './BindMobile'
import WeixinQr from './WeixinQr'
import desktopLib from '../../utils/desktopLib'
import RebindMobile from './RebindMobile'
import { loadTCaptch, loadSMCaptcha } from './captcha'
import SetGroup from './SetGroup'
import { abtest } from '../../utils/sensors'

import arouselImg1 from '../../images/login/login_arousel_1.png'
import arouselImg2 from '../../images/login/login_arousel_2.png'
import arouselImg3 from '../../images/login/login_arousel_3.png'
import arouselImg4 from '../../images/login/login_arousel_4.png'

import { useAppSelector, useAppDispatch } from '../../app/hooks'

// 验证码实例类型
interface CaptchaInstance {
  show?: () => void
  destroy?: () => void
  verify?: () => void
  onSuccess?: (
    callback: (result: { pass: boolean; rid: string }) => void
  ) => void
  onError?: (callback: (errType: string, errMsg: string) => void) => void
  reset?: () => void
}

interface LoginInnerProps {
  onFetchGroupInfo: (options?: {
    isFromBinding?: boolean
    groups?: unknown[]
  }) => void
}

const qrContent = (
  <div className="qr-content">
    <h4>扫码下载</h4>
    <img src={qr_svg} alt="扫码下载" />
  </div>
)

type ValidateStatus = '' | 'success' | 'warning' | 'error' | 'validating'

declare const __BUILD_ENV__: string
declare const __API_SERVER_HOST__: string

// 「模拟登录」HMAC-SHA256 签名：key 明文不出网，只传 hex(HMAC-SHA256(key, ts))
async function hmacSha256Hex(key: string, data: string): Promise<string> {
  const enc = new TextEncoder()
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(data))
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function LoginInner({ onFetchGroupInfo }: LoginInnerProps) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { message } = App.useApp()

  // Redux state
  const isCheckingAuth = useAppSelector((state) => state.main.isCheckingAuth)
  const isDesktop = useAppSelector((state) => state.main.isDesktop)

  // Local state
  const [mobile, setMobile] = useState('')
  const [mobileHelp, setMobileHelp] = useState('')
  const [validateStatus, setValidateStatus] = useState<ValidateStatus>('')
  const [sendVerifyCodeBtnDisabled, setSendVerifyCodeBtnDisabled] =
    useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifyCodeValidateStatus, setVerifyCodeValidateStatus] =
    useState<ValidateStatus>('')
  const [verifyCodeHelp, setVerifyCodeHelp] = useState('')
  const [counting, setCounting] = useState(false)
  const [count, setCount] = useState(59)
  const [isNicknameEmpty, setIsNicknameEmpty] = useState(false)
  const [isBindMobile, setIsBindMobile] = useState(false)
  const [loginByWeixin, setLoginByWeixin] = useState(true)
  const [isChangeMobile, setIsChangeMobile] = useState(false)
  const [isNewGroup, setIsNewGroup] = useState(false)
  const [changeMobileReason, setChangeMobileReason] = useState('')

  // ---- 隐藏「模拟登录」入口 ----
  // logo 3 秒内连点 5 次唤起 modal，普通用户不会触发；即使误触也只是开个对话框，
  // 真正登录还要输入手机号 + 后端 key 鉴权。线上环境同样可用（运营/研发登用户账户看数据）。
  const [mockLoginVisible, setMockLoginVisible] = useState(false)
  const [mockLoginMobile, setMockLoginMobile] = useState('')
  const [mockLoginKey, setMockLoginKey] = useState('')
  const [mockLoginSubmitting, setMockLoginSubmitting] = useState(false)

  // Refs
  const tencentCaptchaInstanceRef = useRef<CaptchaInstance | null>(null)
  const SMCaptchaInstanceRef = useRef<CaptchaInstance | null>(null)
  const SMCaptchaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onekeyTokenRef = useRef<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const userIdRef = useRef<string | null>(null)
  const groupIdRef = useRef<string | null>(null)
  // logo 连击计数 + 计时器（3s 内累计 5 次触发模拟登录 modal）
  const mockLoginClickCountRef = useRef(0)
  const mockLoginClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  // 手机号最新值镜像：腾讯/数美验证码回调由 SDK 注册时持有、闭包固化（见
  // handleTCaptchaCallback 依赖为空），直接闭包读 mobile 会拿到初始空值，
  // 导致加密请求 mobile 为空 → 后端报 SendVerifyCodeRequestSafe.Mobile required。
  // 等价于基准版类组件里的 this.state.mobile（取值时永远是最新）。
  const mobileRef = useRef('')
  mobileRef.current = mobile

  // 处理腾讯云验证码回调
  const handleTCaptchaCallback = useCallback(
    (res: {
      ret: number
      ticket?: string
      randstr?: string
      errorCode?: string
      errorMessage?: string
    }) => {
      console.log('tCaptcha result', res)
      if (res.ret === 0) {
        handleFetchVerifyCode({ ticket: res.ticket, randStr: res.randstr })
        return
      }
      if (res.errorCode) {
        Sentry.captureMessage('tCaptcha error', {
          extra: {
            errorCode: res.errorCode,
            errorMessage: res.errorMessage
          }
        })
      }
    },
    []
  )

  // 处理获取验证码
  const handleFetchVerifyCode = useCallback(
    (validInfo: { rid?: string; ticket?: string; randStr?: string }) => {
      setVerifyCodeValidateStatus('')
      setMobileHelp('')
      setSendVerifyCodeBtnDisabled(true)

      handleRouteVerifyCodeAction(validInfo)
        .then((data) => {
          switch (data.status) {
            case 0:
              handleCounting()
              break
            case -19:
              setVerifyCodeValidateStatus('error')
              setVerifyCodeHelp('验证码发送失败，请重试')
              break
            case -62:
              setVerifyCodeValidateStatus('error')
              setVerifyCodeHelp('验证码发送失败，请重试')
              break
            case -27:
              setVerifyCodeValidateStatus('error')
              setVerifyCodeHelp('验证码发送次数已超出今日限制，请明日重试')
              break
            default:
              break
          }
        })
        .then(() => {
          setSendVerifyCodeBtnDisabled(false)
        })
        .catch(capturePromiseException)
    },
    [mobile]
  )

  // 路由验证码请求
  const handleRouteVerifyCodeAction = useCallback(
    (validInfo: { rid?: string; ticket?: string; randStr?: string }) => {
      if (validInfo.rid) {
        return dispatch(
          fetchVerifyCodeShuMeiEncrypt({
            mobile: mobileRef.current.trim(),
            ...validInfo
          })
        )
      } else if (validInfo.ticket) {
        return dispatch(
          fetchVerifyCodeV3Encrypt({
            mobile: mobileRef.current.trim(),
            ...validInfo
          })
        )
      }
    },
    [dispatch]
  )

  // 开始倒计时
  const handleCounting = useCallback(() => {
    setCounting(true)
    intervalRef.current = setInterval(() => {
      setCount((prev) => {
        if (prev > 0) {
          return prev - 1
        } else {
          handleClearCounting()
          return 59
        }
      })
    }, 1000)
  }, [])

  // 清除倒计时
  const handleClearCounting = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setCounting(false)
    setCount(59)
  }, [])

  // 验证码验证
  const handleCaptchaValidate = useCallback(() => {
    tencentCaptchaInstanceRef.current?.show?.()

    if (SMCaptchaTimerRef.current) clearTimeout(SMCaptchaTimerRef.current)
    SMCaptchaTimerRef.current = setTimeout(() => {
      tencentCaptchaInstanceRef.current?.destroy?.()
      SMCaptchaInstanceRef.current?.verify?.()
    }, 5000)
  }, [])

  // 检查手机号
  const handleCheckMobile = useCallback(() => {
    if (CONFIG.mobileRegex.test(mobile.trim())) {
      setValidateStatus('success')
      setMobileHelp('')
      handleCaptchaValidate()
    } else {
      setValidateStatus('error')
      setMobileHelp('请输入正确的手机号')
      setSendVerifyCodeBtnDisabled(false)
    }
  }, [mobile, handleCaptchaValidate])

  // 手机号登录
  const handleMobileLogin = useCallback(() => {
    sensors.track('web_workgroup_click_mobile_login')
    dispatch(
      loginWithMobileEncrypt({
        mobile: mobile,
        vericode: verifyCode
      })
    )
      .then((data) => {
        if (data.status === -1) {
          sensors.track('web_workgroup_mobile_login_fail', { reason: 'api' })
          message.error('验证码错误，请检查验证码')
        } else if (data.status === -17) {
          sensors.track('web_workgroup_mobile_login_fail', { reason: 'verify' })
          message.error('验证码错误，请检查验证码')
        } else if (data.isNewGroup) {
          userIdRef.current = data.user_id
          groupIdRef.current = data.groups.length && data.groups[0].group_id

          setIsNewGroup(true)
        } else {
          if (isDesktop) {
            desktopLib.loginSuccess()
            return
          }
          message.success('登录成功，欢迎你')
          sensors.login(data.user_id)
          sensors.track('web_workgroup_mobile_login_suc')
          // 不提前设置 group_id，由 handleAfterLogin 按个人空间开通状态决定路由
          onFetchGroupInfo({ groups: data.groups || [] })
        }
      })
      .catch((err) => {
        sensors.track('web_workgroup_mobile_login_fail', { reason: 'others' })
        message.error('登录失败，请重试')
        capturePromiseException(err)
      })
  }, [dispatch, mobile, verifyCode, isDesktop, navigate, onFetchGroupInfo])

  // ---- 隐藏「模拟登录」入口 ----
  // logo 3 秒内连点 5 次 → 弹 modal。普通用户不会乱点 logo，即使误触也只是开个对话框，
  // 真正登录还要输入手机号 + 后端 key 鉴权。
  const handleLogoClick = useCallback(() => {
    mockLoginClickCountRef.current += 1
    if (mockLoginClickTimerRef.current) {
      clearTimeout(mockLoginClickTimerRef.current)
    }
    mockLoginClickTimerRef.current = setTimeout(() => {
      mockLoginClickCountRef.current = 0
    }, 3000)
    if (mockLoginClickCountRef.current >= 5) {
      mockLoginClickCountRef.current = 0
      if (mockLoginClickTimerRef.current) {
        clearTimeout(mockLoginClickTimerRef.current)
        mockLoginClickTimerRef.current = null
      }
      setMockLoginVisible(true)
    }
  }, [])

  const closeMockLogin = useCallback(() => {
    setMockLoginVisible(false)
    setMockLoginMobile('')
    setMockLoginKey('')
  }, [])

  // 模拟登录提交：走 HMAC-SHA256 签名（key 明文不出网）+ 服务端 ±120s 时间窗，
  // 跳过短信验证码，直接 issue cookie；成功后复用正常登录后续流程。
  const handleMockLoginSubmit = useCallback(async () => {
    const trimmedMobile = mockLoginMobile.trim()
    const trimmedKey = mockLoginKey.trim()
    if (!/^1\d{10}$/.test(trimmedMobile)) {
      message.warning('手机号格式不对')
      return
    }
    if (!trimmedKey) {
      message.warning('请填写 key')
      return
    }
    setMockLoginSubmitting(true)
    try {
      const ts = String(Date.now())
      const sign = await hmacSha256Hex(trimmedKey, ts)
      const res = await fetch(
        __API_SERVER_HOST__ + '/web/workgroup/v3/login/with/mock-vericode',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Xhey-Mock-Ts': ts,
            'X-Xhey-Mock-Sign': sign
          },
          body: JSON.stringify({ mobile: trimmedMobile, vericode: '0000' })
        }
      )
      if (res.status === 403) {
        message.error('模拟登录被拒绝（鉴权失败或时钟漂移超 120s）')
        return
      }
      if (!res.ok) {
        message.error(`模拟登录失败：HTTP ${res.status}`)
        return
      }
      const json = await res.json().catch(() => null)
      if (!json || (json.code != null && json.code !== 200)) {
        message.error(
          `模拟登录失败：${json?.message || json?.msg || '服务端无响应'}`
        )
        return
      }
      const data = json.data || json
      // 走与正常登录一样的后续流程，复用 handleMobileLogin 的 happy path
      if (data.isNewGroup) {
        userIdRef.current = data.user_id
        groupIdRef.current =
          data.groups && data.groups[0] && data.groups[0].group_id
        setMockLoginVisible(false)
        setMockLoginKey('')
        setIsNewGroup(true)
        return
      }
      if (isDesktop) {
        desktopLib.loginSuccess()
        return
      }
      message.success(`已模拟登录：${trimmedMobile}`)
      sensors.login(data.user_id)
      closeMockLogin()
      onFetchGroupInfo({ groups: data.groups || [] })
    } catch (err) {
      message.error('模拟登录失败，请检查接口是否就绪')
      capturePromiseException(err)
    } finally {
      setMockLoginSubmitting(false)
    }
  }, [
    mockLoginMobile,
    mockLoginKey,
    message,
    isDesktop,
    onFetchGroupInfo,
    closeMockLogin
  ])

  // 完成团队信息
  const handleFinishGroupInfo = useCallback(
    ({ groupName, industryType, industryName }) => {
      sensors.track('web_click_page_create_group', {
        clickItem: 'createGroup',
        groupName,
        industryName,
        groupID: groupIdRef.current,
        industryCode: industryType
      })

      dispatch(
        finishGroupInfo({
          userID: userIdRef.current,
          groupID: groupIdRef.current,
          groupName,
          industryType,
          source: 0
        })
      )
        .then((data) => {
          if (isDesktop) {
            desktopLib.loginSuccess()
            return
          }

          message.success('登录成功，欢迎你')
          if (data.groups.length === 1) {
            localStorage.setItem('group_id', data.groups[0].group_id)
            dispatch(setGroupId(data.groups[0].group_id))
          }
          onFetchGroupInfo()
        })
        .catch(capturePromiseException)
    },
    [dispatch, isDesktop, onFetchGroupInfo]
  )

  // 完成昵称
  const handleFinishNickname = useCallback(
    (nickname) => {
      dispatch(
        finishNickname({
          userID: userIdRef.current,
          nickname
        })
      )
        .then((data) => {
          if (isDesktop) {
            desktopLib.loginSuccess()
            return
          }
          message.success('登录成功，欢迎你')
          if (data.groups.length === 1) {
            localStorage.setItem('group_id', data.groups[0].group_id)
            dispatch(setGroupId(data.groups[0].group_id))
          }
          onFetchGroupInfo()
        })
        .catch(capturePromiseException)
    },
    [dispatch, isDesktop, onFetchGroupInfo]
  )

  // 微信绑定手机号
  const handleWechatBindMobile = useCallback(
    (mobileNum, verifyCodeNum) => {
      dispatch(
        verifySMSCode({
          mobile: mobileNum,
          vericode: verifyCodeNum
        })
      )
        .then((data) => {
          if (data.status === -1) {
            message.error('验证码错误，请检查验证码')
          } else if (data.status === -14) {
            message.error('该手机号已被其他微信绑定')
            sensors.track('web_show_workgroup_has_bound_phone_toast')
          } else if (data.status === -17) {
            message.error('验证码错误，请检查验证码')
          } else if (!data.user_id) {
            setIsChangeMobile(true)
            setChangeMobileReason('noAccount')
          } else if (!data.groups || !data.groups.length) {
            setIsChangeMobile(true)
            setChangeMobileReason('noGroup')
          } else {
            return dispatch(wechatBindMobile(onekeyTokenRef.current)).then(
              (data) => {
                if (data.nickname === '') {
                  userIdRef.current = data.user_id
                  sensors.track('web_enter_workgroup_login_profile_page')
                  setIsNicknameEmpty(true)
                } else {
                  sensors.login(data.user_id)
                  sensors.track('web_workgroup_wechat_login_suc')
                  Sentry.configureScope((scope) => {
                    scope.setUser({
                      id: data.user_id,
                      nickname: data.nickname,
                      mobile: data.mobile,
                      status: data.status
                    })
                  })

                  sensors.track('web_workgroup_login_bind_phone_succ')

                  onFetchGroupInfo({
                    isFromBinding: true
                  })
                }
              }
            )
          }
        })
        .catch((err) => {
          message.error('绑定失败，请重试')
          capturePromiseException(err)
        })
    },
    [dispatch, onFetchGroupInfo]
  )

  // 微信登录成功回调
  const handleWechatLogin = useCallback(
    (data) => {
      if (!!data.nickname) {
        if (isDesktop) {
          desktopLib.loginSuccess()
          return
        }
        message.success('登录成功，欢迎你')
        sensors.login(data.user_id)
        sensors.track('web_workgroup_wechat_login_suc')
        Sentry.configureScope((scope) => {
          scope.setUser({
            id: data.user_id,
            nickname: data.nickname,
            mobile: data.mobile,
            status: data.status
          })
        })

        // 不提前设置 group_id，由 handleAfterLogin 按个人空间开通状态决定路由
        onFetchGroupInfo({ groups: data.groups || [] })
      } else if (!data.user_id) {
        sensors.track('web_enter_workgroup_login_bind_phone_page', {
          popType: '0'
        })
        setLoginByWeixin(false)
        setIsBindMobile(true)
      } else if (data.nickname === '') {
        userIdRef.current = data.user_id
        sensors.track('web_enter_workgroup_login_profile_page')
        setIsNicknameEmpty(true)
      }
    },
    [dispatch, isDesktop, navigate, onFetchGroupInfo]
  )

  // 下载桌面端
  const handleDownloadDesktop = useCallback(() => {
    window.location.href = CONFIG.DESKTOP_DOWNLOAD_URL
    sensors.track('web_workgroup_login_page_click_client')
  }, [])

  // componentDidMount
  useEffect(() => {
    // 处理来自微信 onekey 回调需要绑定手机号的场景
    const query = parseQueryString()
    if (query && query.token && query.bind === '1') {
      onekeyTokenRef.current = query.token
      setLoginByWeixin(false)
      setIsBindMobile(true)
    }

    abtest.fastFetchABTest({
      param_name: 'webLoginNewAB',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        setLoginByWeixin(res)
        if (res) {
          sensors.track('web_workgroup_click_wechat_login', {
            sourceType: '默认'
          })
        }
      }
    })

    const tCaptchaOptions = {
      loading: true,
      ready: (sdkView: unknown) => {
        if (SMCaptchaTimerRef.current) clearTimeout(SMCaptchaTimerRef.current)
        console.log('tCaptcha ready', sdkView)
      }
    }

    // 加载并初始化腾讯验证码
    loadTCaptch({
      options: tCaptchaOptions,
      callback: handleTCaptchaCallback
    }).then((tCaptcha) => {
      tencentCaptchaInstanceRef.current = tCaptcha as CaptchaInstance
    })

    // 加载并初始化数美验证码
    loadSMCaptcha().then((SMCaptcha) => {
      SMCaptchaInstanceRef.current = SMCaptcha as CaptchaInstance
      SMCaptchaInstanceRef.current.onSuccess?.((result) => {
        console.log('SMCaptcha result', result)
        SMCaptchaInstanceRef.current?.reset?.()
        if (result.pass) {
          handleFetchVerifyCode({
            rid: result.rid
          })
        }
      })
      SMCaptchaInstanceRef.current.onError?.((errType, errMsg) => {
        SMCaptchaInstanceRef.current?.reset?.()
        Sentry.captureMessage('SMCaptcha error', {
          extra: {
            errType,
            errMsg
          }
        })
      })
    })

    // cleanup
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (SMCaptchaTimerRef.current) clearTimeout(SMCaptchaTimerRef.current)
    }
  }, [handleTCaptchaCallback, handleFetchVerifyCode])

  // componentDidUpdate - 监听 URL 查询参数变化
  useEffect(() => {
    const query = parseQueryString()
    if (
      query &&
      query.token &&
      query.bind === '1' &&
      onekeyTokenRef.current !== query.token
    ) {
      onekeyTokenRef.current = query.token
      setLoginByWeixin(false)
      setIsBindMobile(true)
    }
  }, [location])

  // 渲染登录表单
  const renderLoginForm = () => {
    return (
      <div className="panel" key="login-panel">
        {/* 顶部 tab：扫码登录 / 账号登录 */}
        <div
          className="tabs"
          style={{
            position: 'absolute',
            top: 14,
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          <div
            className={`tab ${loginByWeixin ? 'active' : ''}`}
            onClick={() => {
              setLoginByWeixin(true)
              sensors.track('web_workgroup_click_wechat_login', {
                sourceType: '主动点击'
              })
            }}
          >
            扫码登录
          </div>
          <div
            className={`tab ${!loginByWeixin ? 'active' : ''}`}
            onClick={() => {
              setLoginByWeixin(false)
            }}
          >
            账号登录
          </div>
        </div>

        <img
          className="logo"
          src={todaycam_logo}
          alt="今日水印相机"
          onClick={handleLogoClick}
        />
        <h3 className="title" id="title">
          欢迎登录今日水印相机
        </h3>
        <Form className="login-form">
          <Form.Item validateStatus={validateStatus} help={mobileHelp}>
            <Space.Compact block>
              <Input
                size="large"
                readOnly
                tabIndex={-1}
                value="+86"
                style={{
                  width: 53,
                  flex: 'none',
                  textAlign: 'center',
                  background: 'rgba(0,0,0,0.02)',
                  color: 'rgba(0,0,0,0.88)',
                  cursor: 'default',
                  pointerEvents: 'none'
                }}
              />
              <Input
                size="large"
                prefix={<MobileOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="手机号"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value.trim())
                  setValidateStatus('')
                  setMobileHelp('')
                }}
              />
            </Space.Compact>
          </Form.Item>
          <Form.Item
            validateStatus={verifyCodeValidateStatus}
            help={verifyCodeHelp}
          >
            <Input
              size="large"
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder="验证码"
              pattern="[0-9]*"
              maxLength={4}
              suffix={
                counting ? (
                  <span className="count">重新获取（{count}秒）</span>
                ) : (
                  <a
                    onClick={
                      sendVerifyCodeBtnDisabled ? undefined : handleCheckMobile
                    }
                    style={{
                      cursor: sendVerifyCodeBtnDisabled
                        ? 'not-allowed'
                        : 'pointer',
                      opacity: sendVerifyCodeBtnDisabled ? 0.5 : 1
                    }}
                  >
                    获取验证码
                  </a>
                )
              }
              value={verifyCode}
              onChange={(e) => {
                setVerifyCode(e.target.value.trim())
                setVerifyCodeValidateStatus('')
                setVerifyCodeHelp('')
              }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              className="login-button"
              type="primary"
              disabled={!mobile.trim() || verifyCode.length !== 4}
              onClick={handleMobileLogin}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
        <p className="policy">
          阅读并接受&nbsp;
          <a target="_blank" href="https://www.xhey.top/x-terms">
            服务条款
          </a>
          &nbsp;和&nbsp;
          <a target="_blank" href="https://www.xhey.top/x-policy">
            隐私政策
          </a>
        </p>
        <Popover placement="top" content={qrContent}>
          <p className="qr">
            没有今日水印相机APP？<span>立即下载</span>
          </p>
        </Popover>
        {renderMockLoginModal()}
      </div>
    )
  }

  // 隐藏「模拟登录」modal —— logo 5 连击触发，普通用户看不到。
  // 故意用极简内联样式、不进 antd Modal，降低被自动化爬到的可能性；线上环境同样可用。
  const renderMockLoginModal = () => {
    if (!mockLoginVisible) return null
    const inputStyle: React.CSSProperties = {
      width: '100%',
      height: 36,
      border: '1px solid #d9d9d9',
      borderRadius: 4,
      padding: '0 10px',
      fontSize: 14,
      marginBottom: 16,
      boxSizing: 'border-box'
    }
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            padding: '24px 28px',
            width: 360,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 6,
              color: '#222'
            }}
          >
            模拟登录（{__BUILD_ENV__} 环境）
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#999',
              marginBottom: 16,
              lineHeight: 1.6
            }}
          >
            输入要登录的手机号 + key，跳过短信验证码。
            <br />
            仅供研发/运营联调使用；走 HMAC-SHA256 签名 + 服务端 ±120s 时间窗。
          </div>
          <input
            type="tel"
            value={mockLoginMobile}
            onChange={(e) => setMockLoginMobile(e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleMockLoginSubmit()
              if (e.key === 'Escape') closeMockLogin()
            }}
            placeholder="11 位手机号"
            maxLength={11}
            autoFocus
            style={inputStyle}
          />
          <input
            type="password"
            value={mockLoginKey}
            onChange={(e) => setMockLoginKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleMockLoginSubmit()
              if (e.key === 'Escape') closeMockLogin()
            }}
            placeholder="MOCK_LOGIN_XX_xxxxxxxxxxxxxxxx"
            autoComplete="new-password"
            style={inputStyle}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeMockLogin} disabled={mockLoginSubmitting}>
              取消
            </Button>
            <Button
              type="primary"
              onClick={handleMockLoginSubmit}
              loading={mockLoginSubmitting}
            >
              登录
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // 渲染内容
  const renderContent = () => {
    if (isNicknameEmpty) {
      return <InputName onSubmit={handleFinishNickname} />
    }

    if (isNewGroup) {
      return <SetGroup onSubmit={handleFinishGroupInfo} />
    }

    if (isChangeMobile) {
      return (
        <RebindMobile
          reason={changeMobileReason}
          onClick={() => {
            sensors.track('web_enter_workgroup_login_bind_phone_page', {
              popType: changeMobileReason === 'noAccount' ? '1' : '2'
            })

            setIsChangeMobile(false)
            setIsBindMobile(true)
          }}
        />
      )
    }

    if (isBindMobile) {
      return (
        <BindMobile
          onSubmit={handleWechatBindMobile}
          onBack={() => {
            sensors.track('web_click_page_workgroup_login_bind_phone', {
              clickItem: 'retry'
            })
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname
            )
            setIsBindMobile(false)
            setLoginByWeixin(true)
          }}
        />
      )
    }

    if (loginByWeixin) {
      return (
        <WeixinQr
          onBack={() => setLoginByWeixin(false)}
          onSuccess={handleWechatLogin}
          gotoBind={() => {
            setLoginByWeixin(false)
            setIsBindMobile(true)
          }}
        />
      )
    }
    return renderLoginForm()
  }

  // render
  if (isCheckingAuth !== false) {
    return <Spin size="large" className="loading" />
  } else {
    if (isDesktop || isNicknameEmpty || isChangeMobile || isNewGroup) {
      return (
        <div
          className={classnames('Login', {
            'Login-Desktop': isDesktop,
            center: !isDesktop
          })}
        >
          <div className="brand">
            <img
              className="todaycam_logo"
              src={todaycam_logo}
              alt="今日水印相机管理后台"
            />
            <h3>今日水印相机</h3>
          </div>
          {!desktopLib.isDesktop && (
            <img
              className="download-client"
              onClick={() => {
                handleDownloadDesktop()
              }}
              src={download_client}
              alt=""
            />
          )}
          {renderContent()}
        </div>
      )
    }

    return (
      <div className="Login">
        <div className="left">
          <div className="logo-container">
            <img src={logo} className="logo" />
          </div>
          <div className="content-container">
            <div className="carousel-container">
              <Carousel autoplay={true}>
                {[
                  {
                    img: arouselImg1,
                    text: '手机拍照，团队数据实时同步'
                  },
                  { img: arouselImg2, text: '海量照片，智能分类整理' },
                  { img: arouselImg3, text: '团队水印，统一拍摄管理' },
                  { img: arouselImg4, text: '行业数据沉淀，智能规划未来' }
                ].map(({ img, text }, index) => {
                  return (
                    <div key={index} className="carousel-pane">
                      <img
                        className="banner"
                        src={img}
                        style={{
                          width: '100%'
                        }}
                      />
                      <div className="slogan">{text}</div>
                    </div>
                  )
                })}
              </Carousel>
            </div>
          </div>
        </div>

        <div className="right">
          <div className="header">
            <span className="tel">{CONFIG.CUSTOMER_PHONE}</span>
            <img
              className="download"
              onClick={() => {
                handleDownloadDesktop()
              }}
              src={download_client}
              alt=""
            />
          </div>

          <div className="content-container">{renderContent()}</div>
        </div>

        <div className="icp-beian">
          <a href="https://beian.miit.gov.cn" target="_blank">
            蜀ICP备2023006551号-2
          </a>
        </div>
      </div>
    )
  }
}

// 使用 AntdProvider 包裹，确保 App.useApp() 能正常工作
function Login(props) {
  return (
    <AntdProvider>
      <LoginInner {...props} />
    </AntdProvider>
  )
}

export default Login
